import { type ReactNode, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives/responsive";

import { DataEventProps, Row } from "@/libraries/wpl/foundations/src";

const wrapperStyle = css({
    ["@keyframes progressgrow"]: {
        ["0%"]: {
            height: "0%"
        },
        ["100%"]: {
            height: "100%"
        }
    }
});

const contentWrapper = css({
    display: "flex",
    flexDirection: "row",
    gap: 80
});

const textItemStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: 32,
    flex: "0 0 100%",
    [media.above.md]: {
        flex: "0 0 35%"
    }
});
const graphicStyles = css({
    flex: "1"
});

const itemContainer = css({
    position: "relative"
});

const itemStyles = css({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    textAlign: "start",
    paddingInlineStart: 24
});

const buttonStyle = css({
    textAlign: "left",
    background: "none",
    appearance: "none",
    inset: 0,
    cursor: "pointer",
    border: 0,
    paddingInline: 0,
    paddingBlock: 0
});

const headingStyle = css({
    paddingBlockEnd: 24
});

const noMarginStyle = css({
    margin: 0
});

const textLinkStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 8,
    transition: "max-height,padding-block 0.5s",
    maxHeight: 0,
    margin: 0,
    overflow: "hidden"
});

const visibleTextLink = css({
    maxHeight: 180,
    paddingBlock: 4
});

const progressBar = css({
    position: "absolute",
    insetBlockStart: 0,
    insetBlockEnd: 0,
    insetInlineStart: 0,
    width: 3,
    background: "#C1C7D0"
});

const barFillStyle = css({
    background: "#0065FF",
    height: "0%"
});

const fullBarStyle = css({
    height: "100%"
});

const mobileContentStyles = css({
    transition: "opacity 0.5s",
    display: "none",
    overflow: "hidden",
    opacity: 0,
    paddingBlock: 16
});

const contentStyles = css({
    transition: "opacity 0.5s",
    transitionDelay: "0.1s",
    height: 0,
    opacity: 0,
    overflow: "hidden",
    [media.below.md]: {
        display: "none"
    }
});

const activeContentMedia = css({
    opacity: 1,
    [media.below.md]: {
        display: "block"
    }
});

const activeContentStyle = css({
    height: "auto",
    opacity: 1,
    [media.above.md]: {
        display: "block"
    }
});

export type ListContentItem = {
    title: string;
    description: string;
    expandAriaLabel: string;
    content: ReactNode;
    link?: ReactNode;
    dataEventProps?: DataEventProps;
};

export type ListContentCycleProps = {
    items: ListContentItem[];
    heading?: ReactNode;
    listHeading?: string;
    duration?: number;
    onCycleEnd?: () => void;
};

export const ListContentCycle = ({
    items,
    heading,
    listHeading,
    duration = 5000,
    onCycleEnd
}: ListContentCycleProps) => {
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();
    const [activeItem, setActiveItem] = useState(0);
    const [userSelected, setUserSelected] = useState(false);

    const animation = css({
        animation: `progressgrow linear ${duration}ms 1`
    });

    const changeActive = (index: number, setSelected = false) => {
        if (onCycleEnd && index >= items.length) {
            onCycleEnd();
        }
        if (setSelected) {
            setUserSelected(true);
        }
        setActiveItem(index >= items.length ? 0 : index);
    };

    // resume loop after comes back into view
    useEffect(() => {
        if (sectionIsVisible) {
            setUserSelected(false);
        }
    }, [sectionIsVisible]);

    return (
        <Row>
            <div css={wrapperStyle} ref={scrollRef}>
                {heading && <div css={headingStyle}>{heading}</div>}
                <div css={contentWrapper}>
                    <div css={textItemStyles}>
                        {listHeading && (
                            <h3 css={noMarginStyle}>{listHeading}</h3>
                        )}
                        {items.map((item, index) => {
                            return (
                                <div css={itemContainer} key={index}>
                                    <div css={itemStyles}>
                                        <div css={progressBar}>
                                            <div
                                                css={[
                                                    barFillStyle,
                                                    sectionIsVisible &&
                                                        !userSelected &&
                                                        activeItem === index &&
                                                        animation,
                                                    userSelected &&
                                                        activeItem === index &&
                                                        fullBarStyle
                                                ]}
                                                onAnimationEnd={() => {
                                                    if (userSelected) {
                                                        return;
                                                    }
                                                    changeActive(index + 1);
                                                }}
                                            />
                                        </div>
                                        <button
                                            aria-label={item.expandAriaLabel}
                                            aria-expanded={activeItem === index}
                                            onClick={() => {
                                                changeActive(index, true);
                                            }}
                                            css={buttonStyle}
                                            {...item.dataEventProps}
                                        >
                                            <h4 css={noMarginStyle}>
                                                {item.title}
                                            </h4>
                                        </button>
                                        <div
                                            css={[
                                                textLinkStyle,
                                                activeItem === index &&
                                                    visibleTextLink
                                            ]}
                                        >
                                            <p css={noMarginStyle}>
                                                {item.description}
                                            </p>
                                            {item.link}
                                        </div>
                                    </div>
                                    <div
                                        css={[
                                            mobileContentStyles,
                                            activeItem === index &&
                                                activeContentMedia
                                        ]}
                                    >
                                        {item.content}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div css={graphicStyles}>
                        {items.map((item, index) => {
                            return (
                                <div
                                    key={index}
                                    css={[
                                        contentStyles,
                                        activeItem === index &&
                                            activeContentStyle
                                    ]}
                                >
                                    {item.content}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Row>
    );
};
